import React, { memo } from 'react';
import { Link, useLocation } from 'wouter';
import { cn } from '../lib/utils';

export interface NavItem {
  href: string;
  label: string;
}

export const NAV_ITEMS: NavItem[] = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/services', label: 'Services' },
  { href: '/hosting', label: 'Hosting' },
  { href: '/domain-search', label: 'Domain Search' },
  { href: '/portfolio', label: 'Portfolio' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact', label: 'Contact' },
];

interface NavLinkProps {
  href: string;
  label: string;
  variant?: 'desktop' | 'mobile';
  isDark?: boolean;
  scrolled?: boolean;
  onClick?: () => void;
}

/**
 * Reusable navigation link component.
 * Handles active state styling for both desktop and mobile variants.
 */
const NavLink = memo(function NavLink({
  href,
  label,
  variant = 'desktop',
  isDark = false,
  scrolled = false,
  onClick,
}: NavLinkProps) {
  const [location] = useLocation();
  const isActive = location === href;

  if (variant === 'mobile') {
    return (
      <Link href={href}>
        <span
          className={cn(
            'px-4 py-2 rounded-md block cursor-pointer transition-colors',
            isActive
              ? 'bg-primary/10 text-primary'
              : isDark
                ? 'text-gray-200 hover:bg-gray-800'
                : 'text-gray-800 hover:bg-gray-100'
          )}
          onClick={onClick}
        >
          {label}
        </span>
      </Link>
    );
  }

  // Desktop variant
  return (
    <li>
      <Link
        href={href}
        className={cn(
          'hover:text-primary transition-colors',
          isDark
            ? 'text-gray-200'
            : scrolled
              ? 'text-gray-800'
              : 'text-gray-900',
          isActive && 'text-primary font-medium'
        )}
      >
        {label}
      </Link>
    </li>
  );
});

NavLink.displayName = 'NavLink';

export default NavLink;
